import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { agentsApi, type AgentDefinition, type AgentNote } from "../api/agents";
import { MarkdownBody } from "./MarkdownBody";
import { AgentIcon } from "./AgentIconPicker";
import { cn } from "../lib/utils";
import { ChevronDown, ChevronRight, FileText, BookOpen, Wrench, GitBranch, ScrollText } from "lucide-react";
import type { Agent } from "@ligament/shared";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Threshold {
  key: string;
  label: string;
  field: string;
  condition: string;
  value: number;
  severity: "warning" | "critical";
  monitor?: string;
  note?: string;
}

interface ParsedConfig {
  name?: string;
  type?: string;
  schedule?: string;
  model?: string;
  thresholds?: Threshold[];
  connections?: string[] | { feedsInto?: string[]; readFrom?: string[] };
  [key: string]: unknown;
}

interface ParsedTools {
  lookerExplore?: string;
  dataSource?: string;
  lookerFields?: Record<string, string[]> | string[];
  readFrom?: string[];
  writesTo?: string[];
  restrictions?: {
    phiAccess?: boolean;
    patientIdentifiers?: string;
    writeScope?: string;
    [key: string]: unknown;
  };
}

// ─── Helper: section heading ─────────────────────────────────────────────────

function SectionHeading({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: typeof FileText;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="flex items-center gap-2 pb-2 border-b border-border">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  );
}

// ─── Soul section ─────────────────────────────────────────────────────────────

function SoulSection({ soul }: { soul: string }) {
  return (
    <div className="space-y-3">
      <SectionHeading
        icon={FileText}
        title="Soul"
        subtitle="Agent identity, capabilities, and data rules"
      />
      <div className="prose-sm max-w-none">
        <MarkdownBody>{soul}</MarkdownBody>
      </div>
    </div>
  );
}

// ─── Config section ───────────────────────────────────────────────────────────

function SeverityBadge({ severity }: { severity: "warning" | "critical" }) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide",
        severity === "critical"
          ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
          : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-500"
      )}
    >
      {severity}
    </span>
  );
}

function ConfigSection({ config }: { config: ParsedConfig }) {
  const { thresholds, schedule, model, type } = config;

  // connections may be string[] (old) or { feedsInto, readFrom } (new)
  const connections: string[] = Array.isArray(config.connections)
    ? config.connections
    : config.connections
      ? [
          ...((config.connections as { feedsInto?: string[] }).feedsInto ?? []),
          ...((config.connections as { readFrom?: string[] }).readFrom ?? []),
        ]
      : [];

  return (
    <div className="space-y-4">
      <SectionHeading
        icon={BookOpen}
        title="Config"
        subtitle="Static metadata, thresholds, and schedule"
      />

      {/* Metadata row */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        {type && (
          <span>
            <span className="font-medium text-foreground">Type:</span> {type}
          </span>
        )}
        {schedule && (
          <span>
            <span className="font-medium text-foreground">Schedule:</span> {schedule}
          </span>
        )}
        {model && (
          <span>
            <span className="font-medium text-foreground">Model:</span> {model}
          </span>
        )}
      </div>

      {/* Thresholds table */}
      {thresholds && thresholds.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
            KPI Thresholds
          </p>
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Monitor</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Metric</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Condition</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Severity</th>
                </tr>
              </thead>
              <tbody>
                {thresholds.map((t, i) => (
                  <tr key={t.key} className={cn("border-t border-border", i % 2 === 0 ? "bg-transparent" : "bg-muted/20")}>
                    <td className="px-3 py-2 font-mono text-muted-foreground">{t.monitor ?? "—"}</td>
                    <td className="px-3 py-2 font-medium">{t.label}</td>
                    <td className="px-3 py-2 font-mono">
                      {t.condition} {t.value}%
                    </td>
                    <td className="px-3 py-2">
                      <SeverityBadge severity={t.severity} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Connections */}
      {connections && connections.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
            Connections
          </p>
          <div className="flex flex-wrap gap-1.5">
            {connections.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent text-xs font-medium"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tools section ────────────────────────────────────────────────────────────

function ToolsSection({ tools }: { tools: ParsedTools }) {
  const { lookerExplore, dataSource, lookerFields, readFrom, writesTo, restrictions } = tools;

  return (
    <div className="space-y-4">
      <SectionHeading
        icon={Wrench}
        title="Tools"
        subtitle="Data sources, Looker fields, and inter-agent access"
      />

      {/* Explore + data source */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        {lookerExplore && (
          <span>
            <span className="font-medium text-foreground">Explore:</span> {lookerExplore}
          </span>
        )}
        {dataSource && (
          <span>
            <span className="font-medium text-foreground">Table:</span>{" "}
            <code className="font-mono text-[11px]">{dataSource}</code>
          </span>
        )}
      </div>

      {/* Looker fields by view */}
      {lookerFields && (Array.isArray(lookerFields) ? lookerFields.length > 0 : Object.keys(lookerFields).length > 0) && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
            Looker Fields
          </p>
          {Array.isArray(lookerFields) ? (
            <div className="flex flex-wrap gap-1">
              {lookerFields.map((f) => (
                <span
                  key={f}
                  className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono"
                >
                  {f}
                </span>
              ))}
            </div>
          ) : (
          <div className="space-y-3">
            {Object.entries(lookerFields as Record<string, string[]>).map(([viewName, fields]) => (
              <div key={viewName}>
                <p className="text-[11px] font-mono font-medium text-muted-foreground mb-1.5">
                  {viewName}
                </p>
                <div className="flex flex-wrap gap-1">
                  {(Array.isArray(fields) ? fields : []).map((f) => (
                    <span
                      key={f}
                      className="inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Restrictions */}
      {restrictions && (
        <div className="rounded-md border border-border bg-muted/30 p-3 space-y-1 text-xs">
          <p className="font-medium text-muted-foreground uppercase tracking-wide text-[10px] mb-1.5">
            Restrictions
          </p>
          {restrictions.phiAccess === false && (
            <p>
              <span className="font-medium">PHI access:</span>{" "}
              <span className="text-green-600 dark:text-green-400">No direct PHI</span>
            </p>
          )}
          {restrictions.patientIdentifiers && (
            <p>
              <span className="font-medium">Patient IDs:</span> {restrictions.patientIdentifiers}
            </p>
          )}
          {restrictions.writeScope && (
            <p>
              <span className="font-medium">Write scope:</span>{" "}
              <code className="font-mono text-[11px]">{restrictions.writeScope}</code>
            </p>
          )}
        </div>
      )}

      {/* Read from / writes to */}
      {((readFrom && readFrom.length > 0) || (writesTo && writesTo.length > 0)) && (
        <div className="flex gap-6">
          {readFrom && readFrom.length > 0 && (
            <div>
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Reads from
              </p>
              <div className="flex flex-wrap gap-1">
                {readFrom.map((r) => (
                  <span key={r} className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[10px] font-medium">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}
          {writesTo && writesTo.length > 0 && (
            <div>
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Writes to
              </p>
              <div className="flex flex-wrap gap-1">
                {writesTo.map((w) => (
                  <span key={w} className="px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-[10px] font-medium">
                    {w}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Connections flow ─────────────────────────────────────────────────────────

function ConnectionsSection({
  agent,
  readFrom,
  writesTo,
}: {
  agent: Agent;
  readFrom: string[];
  writesTo: string[];
}) {
  const allUpstream = readFrom;
  const allDownstream = writesTo;
  const hasConnections = allUpstream.length > 0 || allDownstream.length > 0;

  return (
    <div className="space-y-4">
      <SectionHeading
        icon={GitBranch}
        title="Connections"
        subtitle="Inter-agent data flow"
      />
      {!hasConnections ? (
        <p className="text-sm text-muted-foreground">
          Standalone monitor — no upstream or downstream agents.
        </p>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          {allUpstream.length > 0 && (
            <>
              <div className="flex flex-col gap-1">
                {allUpstream.map((a) => (
                  <div
                    key={a}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-medium"
                  >
                    <AgentIcon icon={undefined} className="h-3 w-3 text-muted-foreground" />
                    {a}
                  </div>
                ))}
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </>
          )}

          {/* This agent (center) */}
          <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-foreground/30 bg-accent text-sm font-semibold shrink-0">
            <AgentIcon icon={agent.icon} className="h-4 w-4" />
            {agent.name}
          </div>

          {allDownstream.length > 0 && (
            <>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex flex-col gap-1">
                {allDownstream.map((a) => (
                  <div
                    key={a}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-medium"
                  >
                    <AgentIcon icon={undefined} className="h-3 w-3 text-muted-foreground" />
                    {a}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Journal section ──────────────────────────────────────────────────────────

function NoteRow({ note }: { note: AgentNote }) {
  const [expanded, setExpanded] = useState(false);
  const fileName = note.name;

  return (
    <div className="border border-border rounded-md overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-accent/50 transition-colors"
      >
        <ChevronRight
          className={cn("h-3.5 w-3.5 text-muted-foreground transition-transform shrink-0", expanded && "rotate-90")}
        />
        <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
        <span className="text-xs font-mono truncate flex-1">{fileName}</span>
        {note.updatedAt && (
          <span className="text-[10px] text-muted-foreground shrink-0">
            {new Date(note.updatedAt).toLocaleDateString()}
          </span>
        )}
      </button>
      {expanded && (
        <div className="px-4 py-3 border-t border-border bg-muted/20 text-xs">
          <p className="text-muted-foreground font-mono">{note.path}</p>
        </div>
      )}
    </div>
  );
}

function JournalSection({ notes }: { notes: AgentNote[] }) {
  const jobNotes = notes.filter((n) => n.section === "Jobs");
  const learningNotes = notes.filter((n) => n.section === "Learnings");

  return (
    <div className="space-y-4">
      <SectionHeading
        icon={ScrollText}
        title="Journal"
        subtitle="Run history and learnings from the agent's vault"
      />

      {notes.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No journal entries yet. Entries are written after each run and learning event.
        </p>
      )}

      {jobNotes.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
            Run History
          </p>
          <div className="space-y-1.5">
            {jobNotes.map((n) => (
              <NoteRow key={n.path} note={n} />
            ))}
          </div>
        </div>
      )}

      {learningNotes.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
            Learnings
          </p>
          <div className="space-y-1.5">
            {learningNotes.map((n) => (
              <NoteRow key={n.path} note={n} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Notes normalizer ─────────────────────────────────────────────────────────

/** Normalize the notes API response — may be AgentNote[] (old) or an object
 *  with { jobs: [{name, content}], learnings: {...}, profile: string } (new). */
function normalizeNotes(raw: unknown): AgentNote[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as AgentNote[];
  const obj = raw as Record<string, unknown>;
  const result: AgentNote[] = [];
  if (Array.isArray(obj.jobs)) {
    for (const j of obj.jobs as Array<{ name?: string; content?: string }>) {
      result.push({ path: j.name ?? "", name: j.name ?? "", section: "Jobs", updatedAt: null });
    }
  }
  if (obj.learnings && typeof obj.learnings === "object") {
    for (const [key] of Object.entries(obj.learnings as Record<string, unknown>)) {
      result.push({ path: key, name: `${key}.md`, section: "Learnings", updatedAt: null });
    }
  }
  return result;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AgentDefinitionTab({
  agent,
  companyId,
}: {
  agent: Agent;
  companyId: string;
}) {
  const { data: definition, isLoading: defLoading } = useQuery({
    queryKey: ["agent-definition", agent.id],
    queryFn: () => agentsApi.getDefinition(agent.id, companyId),
    staleTime: 60_000,
  });

  const { data: notes } = useQuery({
    queryKey: ["agent-notes", agent.id],
    queryFn: () => agentsApi.getNotes(agent.id, companyId),
    staleTime: 30_000,
  });

  if (defLoading) {
    return (
      <div className="space-y-4 py-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-lg bg-muted/40 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!definition) {
    return (
      <div className="rounded-lg border border-border bg-muted/20 p-6 text-center">
        <FileText className="mx-auto h-8 w-8 text-muted-foreground/40 mb-2" />
        <p className="text-sm font-medium text-muted-foreground">No definition file found for this agent.</p>
        <p className="text-xs text-muted-foreground mt-1">
          Add a <code className="font-mono text-[11px]">metadata.folderKey</code> to this agent to link it to an{" "}
          <code className="font-mono text-[11px]">agents/</code> folder.
        </p>
      </div>
    );
  }

  let parsedConfig: ParsedConfig | null = null;
  let parsedTools: ParsedTools | null = null;
  try {
    if (definition.config) parsedConfig = JSON.parse(definition.config);
  } catch { /* ignore */ }
  try {
    if (definition.tools) parsedTools = JSON.parse(definition.tools);
  } catch { /* ignore */ }

  return (
    <div className="space-y-10 py-2">
      {/* Soul */}
      {definition.soul && <SoulSection soul={definition.soul} />}

      {/* Config */}
      {parsedConfig && <ConfigSection config={parsedConfig} />}

      {/* Tools */}
      {parsedTools && <ToolsSection tools={parsedTools} />}

      {/* Connections */}
      {parsedTools && (
        <ConnectionsSection
          agent={agent}
          readFrom={parsedTools.readFrom ?? []}
          writesTo={parsedTools.writesTo ?? []}
        />
      )}

      {/* Journal */}
      <JournalSection notes={normalizeNotes(notes)} />
    </div>
  );
}
